"use client";
import React from "react";
import { cn } from "utils";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import PriceDisplay from "../../PriceDisplay";
import { PriceFormatResult } from "../../PriceDisplay/types";
import { PriceChangeStatus } from "./types";
import { PriceData } from "@/src/hooks/useTokenPopoverData/types";
import { formatBigNumber } from "@utils/formatBigNumber";
import { getRegionCurrencySymbol } from "@utils/stock";

interface PriceSectionProps {
  title?: string;
  price: PriceData;
}

// 颜色和图标配置映射
const changeStatusStyles: Record<
  PriceChangeStatus,
  { textColor: string; iconName: string }
> = {
  up: {
    textColor: "text-content_accent1_primary",
    iconName: "upvote",
  },
  down: {
    textColor: "text-content_accent2_primary",
    iconName: "downvote",
  },
  flat: {
    textColor: "text-content_secondary",
    iconName: "", // 平盘不显示图标
  },
};

// 价格显示组件
const PriceDisplayContent: React.FC<{ price: PriceData }> = ({ price }) => {
  const styles = changeStatusStyles[price.change.status];

  return (
    <div className={cn("inline-flex flex-col items-start gap-0.5")}>
      <div className={cn("inline-flex flex-col items-start gap-2")}>
        {price.current.type === "crypto" && (
          <PriceDisplay
            priceData={price.current.priceData as PriceFormatResult}
            classNames={{
              container:
                "headline_regular_l text-content_primary whitespace-nowrap",
            }}
          />
        )}
        {price.current.type === "stock" && (
          <div className="headline_regular_m text-content_primary">
            {getRegionCurrencySymbol(price.current.currency)}
            {formatBigNumber(price.current.current_price, {
              needFormat: true,
              trimZeros: true,
              precision: 2,
            })}
          </div>
        )}
        {styles.iconName && (
          <div className={cn("inline-flex items-center gap-1")}>
            <div className={cn("inline-flex items-center gap-0.5 rounded-7.5")}>
              {styles.iconName && (
                <OspIcons
                  name={styles.iconName}
                  size={12}
                  className={styles.textColor}
                />
              )}
              <div
                className={cn(
                  "body_medium_m whitespace-nowrap",
                  styles.textColor,
                )}
              >
                {price.change.percentage}
              </div>
            </div>
            <div className={cn("body_m text-content_tertiary")}>
              {price.current.marketChangeLabel}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const PriceSection: React.FC<PriceSectionProps> = ({ title, price }) => {
  return (
    <div
      className={cn("inline-flex flex-col items-start justify-center gap-2")}
    >
      {title && (
        <div className={cn("body_m text-content_tertiary")}>{title}</div>
      )}

      <PriceDisplayContent price={price} />
    </div>
  );
};
